import { FC } from 'react';
import { ApolloError } from '@apollo/client';
import has from 'lodash/has';
import * as K from 'common/constants';
import ELEMENT from 'common/element';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Link from 'components/common/Link/Link';
import Page from 'components/common/Page/Page';
import { useLogout } from 'hooks/useLogout';

type ErrorLinkAccountProps = {
  error?: ApolloError;
  isNoToken?: boolean;
};

export const InviteTokenError: FC<ErrorLinkAccountProps> = ({ error, isNoToken = false }) => {
  const logout = useLogout();

  const errorKey = Object.values(K.ACCEPT_LINK_PARSE_TOKEN_ERRORS).find((value) =>
    has(error, `graphQLErrors[0].details.${value}`),
  );

  let errorMessage = 'Something went wrong. Unexpected error occurred.';
  let secondaryMessage: string | JSX.Element = 'Please contact Support for help.';

  if (isNoToken || errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.missingInvitationUrl) {
    errorMessage = 'No invite token was included in the URL.';
    secondaryMessage = 'Please click on the link in your invitation email again, or contact Support for help.';
  } else if (errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.incorrectInvitee) {
    errorMessage = 'You are logged in to a different account than the account to which the invitation was sent.';
    secondaryMessage = (
      <span>
        Please
        <Button
          className={'m-1'}
          onClick={() => logout()}
          variant={ButtonVariant.PRIMARY}
          testId={ELEMENT.acceptLinkInviteErrorLogout}
        >
          Log Out
        </Button>
        and then click on the link in your invitation email again.
      </span>
    );
  } else if (errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.inviteRequiresLogin) {
    errorMessage = 'You must be logged in to accept this invitation.';
    secondaryMessage = (
      <span>
        Please
        <Link href={K.ROUTE.LOG_IN} className="link" testId={ELEMENT.acceptLinkInviteErrorLogin}>
          {' log in '}
        </Link>
        and then click on the link in your invitation email again.
      </span>
    );
  } else if (errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.invalidInvitee) {
    errorMessage = 'The invitation link is invalid (target account does not exist).';
    secondaryMessage = 'Please contact Support for help.';
  } else if (errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.expiredInvite) {
    errorMessage = 'The invitation has expired.';
    secondaryMessage = 'Please ask the account owner to send you a fresh invitation.';
  } else if (
    errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.badInvitationUrl ||
    errorKey === K.ACCEPT_LINK_PARSE_TOKEN_ERRORS.token_error
  ) {
    errorMessage = 'The invite token could not be parsed.';
    secondaryMessage = 'Please contact Support for help.';
  }

  return (
    <Page docTitle="Link Account" pageModifier="AcceptLinkInvite__Title">
      <PageHeader title="Unable to link your account" />
      <div className="AcceptLinkInvite__Container">
        <h4
          className="AcceptLinkInvite__InviteInfo AcceptLinkInvite__InviteInfo--Bold"
          data-test-id={ELEMENT.acceptLinkInviteTokenError}
          data-no-token={isNoToken}
          data-error-key={errorKey}
        >
          {errorMessage}
        </h4>
        <p>{secondaryMessage}</p>
      </div>
    </Page>
  );
};
